import React from "react";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import { FailoverTableRow } from "../Types";
import { OverviewPanel, PanelLink } from "./OverviewPanel";
import { MaintenanceEntry } from "./overviewModel";

function DateBlock({ date }: { date: Date | null }) {
  if (!date) return <div className="w-11 shrink-0" />;
  return (
    <div className="flex w-11 shrink-0 flex-col items-center rounded-md border border-semantic-border-primary bg-semantic-bg-secondary py-1">
      <span className="text-[10px] font-semibold uppercase text-semantic-text-secondary">
        {date.toLocaleDateString(undefined, { month: "short" })}
      </span>
      <span className="font-heading text-base font-bold leading-none text-semantic-text-primary">
        {date.getDate()}
      </span>
    </div>
  );
}

function MaintenanceRow({
  date,
  description,
  meta,
  tag,
}: {
  date: Date | null;
  description: string;
  meta: string;
  tag?: string;
}) {
  return (
    <li className="flex gap-3 py-3">
      <DateBlock date={date} />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-semibold text-semantic-text-primary">{description}</p>
        <p className="mt-0.5 text-xs text-semantic-text-secondary">
          {tag && (
            <span className="mr-1.5 rounded bg-semantic-bg-brand-secondary px-1.5 py-0.5 font-semibold text-semantic-text-brand">
              {tag}
            </span>
          )}
          {meta}
        </p>
      </div>
    </li>
  );
}

/**
 * What is scheduled from today on. The platform window, when there is one,
 * leads: it touches every customer, and its wording comes from
 * `buildPlatformMaintenanceRow` so it reads the same here as in the classic table.
 */
export function MaintenancePanel({
  entries,
  platformRow,
  isLoading,
  onViewAll,
}: {
  entries: MaintenanceEntry[];
  platformRow: FailoverTableRow | null;
  isLoading: boolean;
  onViewAll: () => void;
}) {
  return (
    <OverviewPanel
      title="Maintenance"
      subtitle="Scheduled from today on"
      action={<PanelLink label="View all" href="/kontrol/outages" onClick={onViewAll} />}
    >
      <MaintenanceBody entries={entries} platformRow={platformRow} isLoading={isLoading} />
    </OverviewPanel>
  );
}

function MaintenanceBody({
  entries,
  platformRow,
  isLoading,
}: {
  entries: MaintenanceEntry[];
  platformRow: FailoverTableRow | null;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-4 py-2" aria-busy="true">
        {[0, 1, 2].map((index) => (
          <SkeletonLine key={index} h={36} decorative />
        ))}
      </div>
    );
  }
  if (entries.length === 0 && !platformRow) {
    return (
      <p className="py-8 text-center text-sm text-semantic-text-secondary">No maintenance scheduled.</p>
    );
  }
  return (
    <ul className="divide-y divide-semantic-border-primary">
      {platformRow && (
        <MaintenanceRow
          date={null}
          description={platformRow.description}
          meta={platformRow.date}
          tag="Platform"
        />
      )}
      {entries.map((entry) => (
        <MaintenanceRow
          key={entry.key}
          date={entry.date}
          description={entry.description}
          meta={[entry.dateLabel, entry.duration, entry.customer].filter(Boolean).join(" · ")}
        />
      ))}
    </ul>
  );
}
